/* HidroVerse SalesFlow — Web Push subscription helpers
 *
 * One public function: `enableWebPush()` — requests permission, subscribes
 * the current browser via the existing service worker, and persists the
 * subscription to the backend.
 *
 * Wired into NotificationsBell: when the user clicks "Enable" we call
 * `enableWebPush()`. When they grant the OS permission once, the bell
 * pings them on assigns even when the tab is closed (provided the SW
 * is registered, i.e. once you deploy a static build — see swRegister.js).
 */

import api from "./api";

/** Convert base64-url public key to the Uint8Array format pushManager.subscribe wants. */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function enableWebPush() {
  if (!pushSupported()) {
    return { ok: false, reason: "unsupported" };
  }
  // Permission must come from a user gesture — caller is responsible.
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: perm };

  // Pull the public key from the server (kept out of bundle so rotating it doesn't need a redeploy).
  let publicKey = "";
  try {
    const { data } = await api.get("/push/public-key");
    publicKey = data?.public_key || "";
    if (!data?.enabled) return { ok: false, reason: "server-disabled" };
  } catch (_) {
    return { ok: false, reason: "no-key" };
  }
  if (!publicKey) return { ok: false, reason: "no-key" };

  // The existing /service-worker.js is registered from index.js — wait for it.
  let reg;
  try {
    reg = await navigator.serviceWorker.ready;
  } catch (_) {
    // Fall back: try registering on the fly. (Useful in dev where we
    // skipped registration via REACT_APP_DISABLE_SW.)
    try {
      reg = await navigator.serviceWorker.register("/service-worker.js");
    } catch (e) {
      return { ok: false, reason: "no-sw" };
    }
  }

  // Reuse an existing sub if the browser already has one (key rotation = unsubscribe first).
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (e) {
      return { ok: false, reason: "subscribe-failed", error: String(e) };
    }
  }

  const json = sub.toJSON();
  try {
    await api.post("/push/subscribe", {
      endpoint: json.endpoint,
      keys: json.keys,
      user_agent: navigator.userAgent,
    });
  } catch (_) {
    return { ok: false, reason: "save-failed" };
  }
  return { ok: true };
}

export async function disableWebPush() {
  if (!pushSupported()) return { ok: true };
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return { ok: true };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (_) {
    /* keep going; we still tell the server */
  }
  try {
    await api.post("/push/unsubscribe", { endpoint });
  } catch (_) {
    /* ignore */
  }
  return { ok: true };
}
